import { useTranslation } from "react-i18next";
import { CircleAlert, CircleCheck, CircleMinus } from "lucide-react";
import styled from "styled-components";
import { ruleChecks, ruleCheckExcess, type RuleCheck } from "./ruleChecks";
import type { RuleRecord } from "./ruleBlocksData";

const Checks = styled.section`
	h3 { margin: 0; font-size: 14px; color: #344054; }
	.reason { padding: 14px 16px; border: 1px solid #f0d4da; border-radius: 10px; background: #fff6f7; margin-bottom: 18px; }
	.reason-heading { display: flex; align-items: center; gap: 8px; color: #a62c45; font-weight: 600; }
	.reason p { margin: 6px 0 0; }
	.join { margin: 5px 0 12px; color: #667085; font-size: 12px; }
	.check-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
	.check { min-width: 0; border: 1px solid #e4e9ef; border-radius: 10px; padding: 14px; background: #fafbfc; }
	.check[data-matched="true"] { background: #fff; border-color: #e7b4bf; }
	.check-head { display: flex; flex-direction: column; gap: 6px; }
	.check-head strong { font-size: 13px; font-weight: 600; }
	.state { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #667085; }
	.check[data-matched="true"] .state { color: #b42342; }
	.state svg { flex-shrink: 0; }
	.numbers { display: flex; gap: 24px; margin: 14px 0; }
	.number { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
	.number strong { font-size: 26px; font-weight: 600; line-height: 1.2; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.number span, .period, .filters { font-size: 11px; color: #667085; }
	.check[data-matched="true"] .actual { color: #b42342; }
	.period { margin: 0 0 8px; }
	.filters { display: flex; flex-wrap: wrap; gap: 5px; border-top: 1px solid #edf0f5; padding-top: 9px; }
	.filter { border: 1px solid #e4e9ef; border-radius: 4px; background: #fff; padding: 2px 5px; overflow-wrap: anywhere; min-width: 0; }
	.filter-join { align-self: center; }
	@media (max-width: 600px) { .check-grid { grid-template-columns: minmax(0, 1fr); } }
`;

export const RuleCheckSummary = ({ record }: { record: RuleRecord }) => {
	const { t } = useTranslation();
	const checks = ruleChecks(record);
	if (!checks.length) return null;
	const snapshot = record.rule_snapshot;
	const join = snapshot && typeof snapshot === "object" && "join" in snapshot ? snapshot.join : undefined;
	const triggers = checks.filter(check => check.matched === true);
	const label = (check: RuleCheck) => check.status ? t(`administration.checkStatus.${check.status}`, { defaultValue: check.status }) : t("administration.checkNumber", { index: check.index + 1 });
	return <Checks aria-label={t("administration.checksTitle")}>
		{triggers.length > 0 && <div className="reason">
			<div className="reason-heading"><CircleAlert size={17} aria-hidden="true" />{t(record.outcome === "blocked" ? "administration.blockReason" : "administration.matchedConditions")}</div>
			{triggers.map(check => {
				const excess = ruleCheckExcess(check);
				return <p key={check.index}>{check.value !== null && check.countMax !== null ? t("administration.reasonLimit", { label: label(check), value: check.value, max: check.countMax }) : `${label(check)}: ${t("administration.checkMatched")}.`}{excess !== null && <> <strong>{t("administration.excessBy", { value: excess })}</strong></>}</p>;
			})}
		</div>}
		<h3>{t("administration.checksTitle")}</h3>
		{(join === "OR" || join === "AND") && <p className="join">{t(`administration.checkLogic.${join}`)}</p>}
		<div className="check-grid">{checks.map(check => <div className="check" key={check.index} data-matched={check.matched === true}>
			<div className="check-head"><strong>{label(check)}</strong><span className="state">
				{check.matched === true ? <CircleAlert size={13} aria-hidden="true" /> : check.matched === false ? <CircleCheck size={13} aria-hidden="true" /> : <CircleMinus size={13} aria-hidden="true" />}
				{t(`administration.${!check.hasResult ? "checkNotReturned" : check.matched === true ? "checkMatched" : check.matched === false ? "checkNotMatched" : "checkUnknown"}`)}
			</span></div>
			<div className="numbers"><div className="number"><strong className="actual">{check.value ?? "—"}</strong><span>{t("administration.observedValue")}</span></div>
				{check.countMax !== null && <div className="number"><strong>{check.countMax}</strong><span>{t("administration.maximumLabel")}</span></div>}
			</div>
			{(check.from || check.to) && <p className="period">{t("administration.checkPeriod")}: {check.from === check.to ? check.from : `${check.from || "—"} — ${check.to || "—"}`}</p>}
			{check.filters.length > 0 && <div className="filters">{check.filters.map((filter, index) => <span className="filter" key={index}>{filter.field}: {filter.value}</span>)}{check.filters.length > 1 && check.matchingJoin && <span className="filter-join">{check.matchingJoin}</span>}</div>}
		</div>)}</div>
	</Checks>;
};
